import { useAtom } from '@reatom/npm-react'
import React, { memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import styled, { css } from 'styled-components'

import { Typography } from '@blade/ui-kit'
import {
  CurrencyIcon,
  CurrencyInformationSocial,
} from '@root/entities/currency/ui'
import { PairBoostersBadge } from '@root/entities/pair/ui'
import { premiumFontCss } from '@root/entities/premium/ui'
import { PairExploreItemMetrics } from '@root/features/explore/ui/pair-explore-item/metrics/pair-explore-item-metrics'
import { PairExploreItemAudit } from '@root/features/explore/ui/pair-explore-item/pair-explore-item-audit'
import { TradeRoute } from '@root/features/routing'
import { QuickBuyButton } from '@root/features/trade/swap/ui/desktop'
import { NumberUtil } from '@root/shared'
import { PriceChartThumbnail, Separator } from '@root/shared/ui-kit'
import { CreatedBy } from '@root/shared/ui-kit/created-by'

import { CurrencyCardEmptyChart } from './currency-card-empty-chart'
import { MarketCap } from './market-cap'

import type { PairModel } from '@root/entities/pair/atoms/reatom-pair'
import type { ClassNameProps } from '@root/shared/react'
import type { UTCTimestamp } from 'lightweight-charts'

const radius = () => 10

type Props = {
  pair: PairModel
} & ClassNameProps

export const CurrencyCard: React.FC<Props> = memo((props) => {
  const { pair, className } = props
  const { t } = useTranslation()
  const [priceChart] = useAtom(pair.priceChartMinutes)
  const [premium] = useAtom(pair.premiumInfo.premium)
  const [socials] = useAtom(pair.quote.socials)
  const [marketCapUsd] = useAtom(pair.marketCapUsd)
  const [icon] = useAtom(pair.quote.icon)

  const candles = useMemo(
    () =>
      priceChart.map((x) => ({
        time: new Date(x.timestamp).getTime() as UTCTimestamp,
        open: NumberUtil.safeParseFloat(x.price.open) ?? 0,
        high: NumberUtil.safeParseFloat(x.price.high) ?? 0,
        low: NumberUtil.safeParseFloat(x.price.low) ?? 0,
        close: NumberUtil.safeParseFloat(x.price.close) ?? 0,
      })),
    [priceChart],
  )

  return (
    <CurrencyCardRoot
      as={Link}
      to={TradeRoute.build({
        pairAddress: pair.address,
        network: pair.network,
      })}
      className={className}
    >
      <CurrencyCardTop>
        <CurrencyIcon
          currency={pair.quote}
          size={70}
          outline={premium ? 'premium' : 'outline'}
        />
        <CurrencyCardTopInfo>
          <NameWithBoosters>
            <Name ellipsis $premium={premium}>
              {pair.quote.title}
            </Name>
            <PairBoostersBadge
              currency={pair.quote}
              premiumInfo={pair.premiumInfo}
              mode="explore"
            />
          </NameWithBoosters>
          <DetailedCurrencyInfo>
            <DetailedCurrencyInfoSide>
              <Ticker>
                <span>{t('fun.ticker')}:</span>
                <TickerValue>{pair.quote.ticker}</TickerValue>
              </Ticker>
              <PairExploreItemAudit showProvider pair={pair} size={12} />
              <PairExploreItemMetrics
                statsPeriod="5M"
                pair={pair}
                showVolume={false}
              />
            </DetailedCurrencyInfoSide>
            <DetailedCurrencyInfoSide>
              <ChartBuyButton>
                {pair.priceChartMinutes.length > 0 ? (
                  <Chart
                    candles={candles}
                    radius={radius}
                    css={css`
                      z-index: 0;
                    `}
                  />
                ) : (
                  <CurrencyCardEmptyChart />
                )}
                <BuyButton pair={pair} withLabel section="exploreFeeds" />
              </ChartBuyButton>
            </DetailedCurrencyInfoSide>
          </DetailedCurrencyInfo>
        </CurrencyCardTopInfo>
      </CurrencyCardTop>
      <Separator opacityMaxPercent={10} />
      <Body>
        {/* Temp Description */}
        <Description>
          Join the APESEI GANG 🇨🇳 – shill the coin and have fun with the chinese
          brother of Apes! 🇨🇳🐵🙉🙈
        </Description>
        <BodySocials>
          <CreatedBy address={pair.quote.address} avatar={icon} />
          <CurrencyInformationSocial socials={socials} />
        </BodySocials>
      </Body>
      <Separator opacityMaxPercent={10} />
      <MarketCap value={marketCapUsd} />
    </CurrencyCardRoot>
  )
})

export const CurrencyCardRoot = styled.div`
  all: unset;
  border: 1px solid var(--backgroundA-3);
  background: var(--background-2);
  box-sizing: border-box;

  border-radius: 12px;
  position: relative;

  display: grid;
  gap: 8px;
  cursor: pointer;
  padding: 16px 16px 8px 16px;

  transition: all 0.1s ease-in-out;

  &:hover {
    border: 1px solid var(--backgroundA-5);
    background: var(--background-3);
  }

  will-change: border, background, transform;
`

export const CurrencyCardTop = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 15px;
`

export const CurrencyCardTopInfo = styled.div`
  display: grid;
  grid-template-rows: auto 1fr;
`

export const Body = styled.div`
  display: grid;
  gap: 6px;
`

export const BuyButton = styled(QuickBuyButton)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  display: none;

  ${CurrencyCardRoot}:hover & {
    display: flex;
  }
`

export const ChartBuyButton = styled.div`
  display: block;
  position: relative;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  grid-row: span 3;
`

export const Chart = styled(PriceChartThumbnail)`
  width: 100%;
  height: 100%;
  opacity: 1;

  ${CurrencyCardRoot}:hover & {
    opacity: 0.3;
  }
`

export const Name = styled(Typography)<{ $premium: boolean }>`
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  line-height: 20px;
  // Multiline text breaks grid layout
  /* text-wrap: balance; */

  width: 100%;
  overflow: hidden;
  white-space: nowrap;

  ${({ $premium }) => $premium && premiumFontCss}
`

export const Ticker = styled(Typography)`
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  display: grid;
  grid-template-columns: auto 1fr;
  color: var(--text-8);
  font-size: 11px;
  font-weight: 500;
  gap: 4px;
`

export const TickerValue = styled(Typography)`
  color: var(--text-10);
  font-weight: 600;
`

export const NameWithBoosters = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  justify-content: start;
  justify-items: start;
  align-items: center;
  gap: 8px;
`

export const DetailedCurrencyInfo = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 8px;
`

export const DetailedCurrencyInfoSide = styled.div`
  display: grid;
  grid-template-rows: auto auto min-content;
  gap: 4px;
`

export const Description = styled(Typography)`
  color: var(--text-8);
  text-align: center;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;

  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const BodySocials = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  justify-content: center;
  gap: 4px;
`
